import type { KataScoringDefinition, ScoringCriterion } from './types'

function c(
  id: string,
  text: string,
  suggested: ScoringCriterion['suggested'],
  hint?: string,
): ScoringCriterion {
  return hint ? { id, text, suggested, hint } : { id, text, suggested }
}

const generalCeremony: ScoringCriterion[] = [
  c('koshiki-gen-tori-uke-sides', 'Tori on left side of Shomen Uke on the right side', 'forgotten'),
  c('koshiki-gen-standing-bow', 'Standing bow to each other with heels apart', 'small'),
  c('koshiki-gen-kneeling-bow', 'Kneeling bow', 'medium'),
  c(
    'koshiki-gen-shizen-steps',
    'Tori facing Shomen, does 2 steps (shizen-hontai) then another wide step (shizen-hontai)',
    'medium',
  ),
  c('koshiki-gen-kurai-dori', 'Kurai-dori posture', 'small_medium'),
  c('koshiki-gen-dai-no-ji', 'Dai-no-ji posture', 'small_medium'),
  c('koshiki-gen-ukemi', 'Ukemi', 'small_medium'),
  c('koshiki-gen-target', 'Target of attack', 'medium'),
  c('koshiki-gen-distance', 'Distance', 'medium'),
  c('koshiki-gen-interaction', 'Interaction', 'medium'),
]

/** Practice judging: 22 scored elements (max 220); opening/closing bows are catalog-only. */
export const koshikiNoKataScoring: KataScoringDefinition = {
  kataId: 'koshiki-no-kata',
  maxPerJudge: 220,
  maxPanelOfThree: 660,
  elements: [
    {
      id: 'koshiki-general',
      kind: 'ceremony',
      name: 'General elements',
      nameJa: '一般観察事項',
      criteria: generalCeremony,
    },
    {
      id: 'koshiki-tai',
      kind: 'technique',
      name: 'Tai',
      nameJa: '体',
      techniqueId: 'koshiki-tai',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-tai-1',
          'Uke quietly moves forward, starting with his left foot first, does 6 steps and puts his right foot in front of Tori’s left foot (with his right foot right before the Tori’s left foot)',
          'small_medium',
        ),
        c(
          'koshiki-tai-2',
          'Uke grips the back side of Tori’s belt with his right hand and the front side with his left hand',
          'small_medium',
        ),
        c('koshiki-tai-3', 'Uke attempts to hit Tori’s right kneecap with his left foot', 'small_medium'),
        c(
          'koshiki-tai-4',
          'Uke swings back his left foot and strongly pulls both hands and attempts to throw Tori with Koshi-nage',
          'small_medium',
        ),
        c(
          'koshiki-tai-5',
          'Tori inserts his left hand under Uke’s right armpit to hold Uke’s left hip and puts his right palm on Uke’s left chest',
          'small_medium',
        ),
        c(
          'koshiki-tai-6',
          'Tori pushes Uke in tsugi-ashi toward his left rear corner, Uke retreats in ayumi-ashi',
          'small_medium',
        ),
        c(
          'koshiki-tai-7',
          'Tori breaks Uke’s balance backward, puts his right knee down, when being thrown down Uke takes a breakfall',
          'small_medium',
        ),
        c('koshiki-tai-8', 'Tori turns his left foot to the left and take Kurai-dori posture', 'small_medium'),
      ],
    },
    {
      id: 'koshiki-yume-no-uchi',
      kind: 'technique',
      name: 'Muchu',
      nameJa: '夢中',
      techniqueId: 'koshiki-yume-no-uchi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-yume-no-uchi-1',
          'Tori moves to the spot, which is diagonally leftward and slightly behind where he started Tai. Uke returns to the spot where he started Tai',
          'small_medium',
        ),
        c(
          'koshiki-yume-no-uchi-2',
          'Uke does 6 steps to get closer to Tori (his right foot in front of Tori’s left foot). Uke attempts to throw Tori and Tori attempts to control Uke as they did in Tai',
          'small_medium',
        ),
        c(
          'koshiki-yume-no-uchi-3',
          'Uke losing his balance backward, tries to restore his position by slightly retreating with his left foot',
          'small_medium',
        ),
        c(
          'koshiki-yume-no-uchi-4',
          'Tori changes the direction of his attack, moves his left hand to right side of Uke’s back, his right hand on Uke’s upper left arm and lift Uke’s body forward',
          'small_medium',
        ),
        c(
          'koshiki-yume-no-uchi-5',
          'Uke takes a step forward with his left foot. Tori turns his body inside, retreats his left foot beside his right foot. Tori takes a few small steps backward',
          'small_medium',
        ),
        c(
          'koshiki-yume-no-uchi-6',
          'Uke is thrown diagonally over Tori’s body while Tori spreads both arms and legs on the ground (Dai-no-ji)',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-ryokuhi',
      kind: 'technique',
      name: 'Chikara-yoke',
      nameJa: '力避',
      techniqueId: 'koshiki-ryokuhi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-ryokuhi-1',
          'Uke and Tori meet in the center about 60 cm apart. Uke attempts to grip the front side of Tori’s belt from above with both of his hands (right over left)',
          'small_medium',
        ),
        c(
          'koshiki-ryokuhi-2',
          'Tori takes a step slightly backward with his right foot toward his right rear corner. Tori sweeps away Uke’s right wrist from outside to inside with his left hand (with thumb facing upward) and grips Uke’s wrist to pull it forward',
          'small_medium',
        ),
        c(
          'koshiki-ryokuhi-3',
          'Tori puts his right hand (with its back facing downward and thumb upward) over his left hand to grip the upper side of Uke’s right elbow from outside and pulls Uke toward him to bring Uke to his right front corner',
          'small_medium',
        ),
        c(
          'koshiki-ryokuhi-4',
          'Tori drives forward Uke with tsugi-ashi. Tori moves toward the right front corner pushing Uke right elbow upward and controls Uke upper left arm with his left hand',
          'small_medium',
        ),
        c('koshiki-ryokuhi-5', 'Tori moves a few steps and lifts Uke’s body. Uke stands on his toes', 'small_medium'),
        c(
          'koshiki-ryokuhi-6',
          'Tori puts both hands on Uke’s shoulders and pulls him down taking a wide step backward with his left foot and puts his left knee on the ground',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-mizu-guruma',
      kind: 'technique',
      name: 'Mizu-guruma',
      nameJa: '水車',
      techniqueId: 'koshiki-mizu-guruma',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-mizu-guruma-1',
          'Uke and Tori meet in the center about 60 cm apart. Uke attempts to grip the front side of Tori’s belt from above with both of his hands (right over left)',
          'small_medium',
        ),
        c(
          'koshiki-mizu-guruma-2',
          'Tori takes a wide step backward with his right foot. Tori grips Uke’s right wrist with his right hand and puts his left hand slightly above Uke’s right elbow, while trying to pull down Uke diagonally rightward',
          'small_medium',
        ),
        c(
          'koshiki-mizu-guruma-3',
          'Following Uke’s reaction, Tori takes a wide step forward with his right foot toward the outside of Uke’s right foot. Tori moves his left foot toward the lateral side of Uke and turning his body to his right',
          'small_medium',
        ),
        c(
          'koshiki-mizu-guruma-4',
          'Tori presses the back of Uke’s right hand onto Uke’s forehead and uses his left hand to hold the backside of Uke’s hips. Tori pushes Uke backward to break the balance',
          'small_medium',
        ),
        c(
          'koshiki-mizu-guruma-5',
          'Uke losing his balance backward, tries to restore his position by slightly retreating with his left foot. Tori changes the direction of his attack, moves his left hand to right side of Uke’s back, his right hand on Uke’s upper left arm and lift Uke’s body forward to break his balance',
          'small_medium',
        ),
        c(
          'koshiki-mizu-guruma-6',
          'Uke takes a step forward with his left foot. Tori turns his body inside, retreats his left foot beside his right foot. Tori takes a few small steps backward. Uke is thrown diagonally over Tori’s body while Tori spreads both arms and legs on the ground (Dai-no-ji)',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-mizu-nagare',
      kind: 'technique',
      name: 'Mizu-nagare',
      nameJa: '水流',
      techniqueId: 'koshiki-mizu-nagare',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-mizu-nagare-1',
          'Tori and Uke stand in shizen-hontai diagonally at about 5m. Tori is on the right of Shomen. They both take a few steps to get closer to each other. Uke puts his right hand on the right side of his back while gradually raising his left hand with its palm facing downward',
          'small_medium',
        ),
        c(
          'koshiki-mizu-nagare-2',
          'Upon reaching the distance, Uke takes a wide step forward with his left foot and attempts to hook Tori’s collar with four fingers of his left hand. Tori retreats slightly with his right foot in a moment. Uke being unable to hook Tori’s collar, loses balance forward',
          'small_medium',
        ),
        c(
          'koshiki-mizu-nagare-3',
          'Tori quickly retreats with his right and then, left foot by tsugi-ashi, grips Uke’s left hand with his right hand, thumb on top, to pull it up forward and puts his left hand on the bottom side of Uke’s upper arm to push it up',
          'small_medium',
        ),
        c(
          'koshiki-mizu-nagare-4',
          'Tori then puts his left palm on the inside of Uke’s upper arm and push it outward. Tori retreats with his right foot and puts his right knee on the tatami, pulls down Uke forward',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-hiki-otoshi',
      kind: 'technique',
      name: 'Hiki-otoshi',
      nameJa: '曳落',
      techniqueId: 'koshiki-hiki-otoshi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-hiki-otoshi-1',
          'Tori and Uke face each other in shizen-hontai on the opposite sides of position in “Mizu-nagare” with a distance of about 5m. They both take a few steps to get closer to each other',
          'small_medium',
        ),
        c(
          'koshiki-hiki-otoshi-2',
          'Uke steps forward with his right foot and attempts to grab Tori’s left waist with his right hand at belt level',
          'small_medium',
        ),
        c(
          'koshiki-hiki-otoshi-3',
          'Tori avoids Uke’s attack while retreating with his left-right foot by tsugi-ashi. Uke being unable to grab on Tori’s waist, loses balance forward',
          'small_medium',
        ),
        c(
          'koshiki-hiki-otoshi-4',
          'Tori grips Uke’s right wrist from above with his left hand, puts his right hand on Uke’s right upper arm with his thumb facing upward. Tori retreats again with his left foot and puts his left knee on tatami, pulling down Uke forward with both hands',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-ko-daore',
      kind: 'technique',
      name: 'Ko-daore',
      nameJa: '虚倒',
      techniqueId: 'koshiki-ko-daore',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-ko-daore-1',
          'Tori and Uke face each other in shizen-hontai on the opposite sides of position in “Hiki-otoshi” with a distance of about 5m. They both take a few steps to get closer to each other',
          'small_medium',
        ),
        c(
          'koshiki-ko-daore-2',
          'Tori takes a few steps forward, gradually raises his right hand forward, while making Te-gatana (thumb facing upward and four fingers held together and stretched). Tori steps forward with his right foot and attempts to thrust with Te-gatana at Uke’s uto (the middle of eyebrows)',
          'small_medium',
        ),
        c(
          'koshiki-ko-daore-3',
          'Uke turns his body to his right and turning away his face to avoid Tori’s attack. Uke grips Tori’s right wrist with four fingers of his right hand facing upward and pulls it forward. Uke steps in front of Tori’s body with his left foot, inserts his left hand deeply into Tori’s back and holds Tori’s hips toward his left hip, attempts to throw Tori',
          'small_medium',
        ),
        c(
          'koshiki-ko-daore-4',
          'Tori responds to Uke’s attack by stretching his right arm and presses his forearm (with his small finger side) upon Uke’s face to control Uke. Tori puts his right hip on the back side of Uke’s left hip, puts his left hand on the front side of Uke’s belt and moves diagonally backward and rightward with his right foot by tsugi-ashi',
          'small_medium',
        ),
        c(
          'koshiki-ko-daore-5',
          'Tori breaks Uke’s balance to the right rear corner, retreats his left foot to put his left knee close to his right heel and pushes down Uke backward with both of his hands. Tori turns his right foot to the right and takes Kurai-dori posture',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-uchikudaki',
      kind: 'technique',
      name: 'Uchi-kudaki',
      nameJa: '打砕',
      techniqueId: 'koshiki-uchikudaki',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-uchikudaki-1',
          'Tori and Uke face each other in shizen-hontai on the opposite sides of position in “Ko-daore” with a distance of about 5m. They both take a few steps to get closer to each other',
          'small_medium',
        ),
        c(
          'koshiki-uchikudaki-2',
          'Tori takes a few steps forward, gradually raises his left hand forward, while making it Te-gatana (with thumb facing upward and four fingers held together and stretched). Tori steps forward with his left foot and attempts to thrust with Te-gatana at Uke’s abdomen (suigetsu)',
          'small_medium',
        ),
        c(
          'koshiki-uchikudaki-3',
          'Uke turns his body to his left to avoid Tori’s attack. Uke grips Tori’s wrist with four fingers of his left hand facing upward and pulls it forward. Uke steps in front of Tori’s body with his right foot, inserts his right hand deeply into Tori’s back and, while holding Tori’s hip and attempts to throw Tori',
          'small_medium',
        ),
        c(
          'koshiki-uchikudaki-4',
          'Tori stretches his left arm, turns around his wrist (with his small finger facing upward) to hold Uke’s hips from the front side as he scoops it up and puts his right hand on the front side of Uke’s belt. Tori moves his body diagonally backward and leftward with his left foot by tsugi-ashi',
          'small_medium',
        ),
        c(
          'koshiki-uchikudaki-5',
          'Tori pushes Uke to the left rear corner and breaks his balance. Tori retreats his right foot to put his right knee close to his left heel and pushes down Uke backward with both of his hands. Tori turns his left foot to the left and takes Kurai-dori posture',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-tani-otoshi',
      kind: 'technique',
      name: 'Tani-otoshi',
      nameJa: '谷落',
      techniqueId: 'koshiki-tani-otoshi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-tani-otoshi-1',
          'Tori: faces Shomen in shizen-hontai on the right side of the center about 50cm above the kata axis on Shomen side. Uke stands in shizen-hontai (his right foot on the same line as Tori’s left foot) toward Tori’s back about 3m',
          'small_medium',
        ),
        c(
          'koshiki-tani-otoshi-2',
          'Uke moves to reach left behind Tori up to a distance of about 1 step. Uke puts his left foot beside Tori’s left foot. Uke puts his right hand on the back side of Tori’s right shoulder and his left hand on the front side of Tori’s abdomen and attempts to push down Tori to forward',
          'small_medium',
        ),
        c(
          'koshiki-tani-otoshi-3',
          'Tori responds to Uke’s attack by bending his upper body deeply forward. Tori grabs Uke’s right hand from its small finger side with his right hand. Uke steps forward with his right foot. Tori raises his right hand, moves his left hand to Uke’s left hip and moves his left foot behind both of Uke’s feet',
          'small_medium',
        ),
        c(
          'koshiki-tani-otoshi-4',
          'Tori raises Uke’s right hand diagonally upward at a stretch with his right hand to lift Uke’s body. Tori moves his body a few steps diagonally backward and leftward with his left foot by tsugi-ashi. Tori breaks Uke’s balance to the left rear corner, put his right knee close to his left heel. Tori turns his left foot to the left and takes Kurai-dori posture',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-kuruma-daore',
      kind: 'technique',
      name: 'Kuruma-daore',
      nameJa: '車倒',
      techniqueId: 'koshiki-kuruma-daore',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-kuruma-daore-1',
          'Tori: faces Shomen in shizen-hontai on the right side of the center about 50cm behind the kata axis on Shomen side. Uke stands in shizen-hontai (his right foot on the same line as Tori’s left foot) toward Tori’s back about 3m',
          'small_medium',
        ),
        c(
          'koshiki-kuruma-daore-2',
          'Uke raises his both hands and attempts to twist Tori’s body with both hands (right hand on the back side of Tori’s right shoulder and left hand on the front side of Tori’s left shoulder). Tori pivots on his right foot, then steps into the right side of Uke’s right foot with his left and then right feet',
          'small_medium',
        ),
        c(
          'koshiki-kuruma-daore-3',
          'Tori puts both of his hands around Uke’s armpits (thumbs up) to lift and break Uke’s balance. Tori throws Uke over his body to take dai-no-ji posture. Uke jumps over Tori’s body diagonally and stands up',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-shikoro-dori',
      kind: 'technique',
      name: 'Shikoro-dori',
      nameJa: '錣取',
      techniqueId: 'koshiki-shikoro-dori',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-shikoro-dori-1',
          'Tori moves to the center and stands in shizen-hontai, on the left side of Shomen, Uke facing Tori at about 30cm. Uke turns his left palm inside and attempts to grip the front side of Tori’s belt from above. Tori slightly retreats his hips to avoid Uke’s attack',
          'small_medium',
        ),
        c(
          'koshiki-shikoro-dori-2',
          'Tori sweeps and grips Uke’s left wrist with his right hand (with thumb facing upward) and pulls it downward and leftward. Tori presses his left palm on the left side of Uke’s jaw and pushes it up diagonally rightward. Uke pivots on his left foot, turns back with his right foot just beside his left foot and his balance broken backward',
          'small_medium',
        ),
        c(
          'koshiki-shikoro-dori-3',
          'Tori moves his right hand to put on Uke’s right shoulder from behind, also moves his left hand to put on Uke’s left shoulder from the front. Tori takes a wide step backward with his left foot to put his left knee on tatami',
          'small_medium',
        ),
        c(
          'koshiki-shikoro-dori-4',
          'Uke falls down behind by slightly retreating with his right foot, does a ukemi',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-shikoro-gaeshi',
      kind: 'technique',
      name: 'Shikoro-gaeshi',
      nameJa: '錣返',
      techniqueId: 'koshiki-shikoro-gaeshi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-shikoro-gaeshi-1',
          'Tori moves to the center and stands in shizen-hontai, on the right side of Shomen, Uke facing Tori at about 30cm. Uke turns his left palm inside to grip the front side of Tori’s belt from above, taking a wide step backward with his right foot, turning his body to his right. Uke drags Tori’s body toward his left hip',
          'small_medium',
        ),
        c(
          'koshiki-shikoro-gaeshi-2',
          'Tori takes a wide step with his right foot behind Uke. Tori puts his right hand on Uke’s left temple and his left hand on the right side of Uke’s jaw to pull forward and twists Uke’s neck. Tori puts his right hand on Uke’s right shoulder with his fingers facing forward, and his left hand onto Uke’s left shoulder. Tori breaks Uke’s balance backward',
          'small_medium',
        ),
        c(
          'koshiki-shikoro-gaeshi-3',
          'Tori lowers his hips and sweeps Uke’s left leg from behind with the inner side of his right leg. Tori pulls down Uke on his back and sits with both of his legs stretched out. Uke does a ukemi and sits with both of his legs stretched out',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-yudachi',
      kind: 'technique',
      name: 'Yudachi',
      nameJa: '夕立',
      techniqueId: 'koshiki-yudachi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-yudachi-1',
          'Tori moves to the center and stands in shizen-hontai, on the left side of Shomen, Uke facing Tori at about 60cm. Tori grabs both of Uke’s collars with both hands, then puts both of those collars in his right hand to insert his index finger between the collars',
          'small_medium',
        ),
        c(
          'koshiki-yudachi-2',
          'Uke takes a step forward with his left foot and holds the bottom side of Tori’s right elbow with his left hand. Tori retreats with his right foot. Uke takes a wide step forward with his right foot and attempts to grip the left side of Tori’s hip with his right hand and attempts to throw Tori with Koshi-nage',
          'small_medium',
        ),
        c(
          'koshiki-yudachi-3',
          'Tori grabs Uke’s right arm with his left hand, holds Uke’s arm underneath his left armpit. Tori retreats with his left foot to put his left knee on tatami and pulls down Uke forward with both of his hands',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-taki-otoshi',
      kind: 'technique',
      name: 'Taki-otoshi',
      nameJa: '滝落',
      techniqueId: 'koshiki-taki-otoshi',
      groupId: 'omote',
      criteria: [
        c(
          'koshiki-taki-otoshi-1',
          'Tori moves to the center and stands in shizen-hontai, on the right side of Shomen, Uke facing Tori at about 60cm. Tori grabs both of Uke’s collars with both hands, then puts both of those collars in his right hand to inserts his index finger between the collars',
          'small_medium',
        ),
        c(
          'koshiki-taki-otoshi-2',
          'Uke takes a step forward with his left foot and holds the bottom side of Tori’s right elbow with his left hand. Tori retreats with his right foot. Uke takes a wide step forward with his right foot and attempts to grip the back side of Tori’s belt over Tori’s left shoulder with his right hand and attempts to throw Tori with Koshi-nage',
          'small_medium',
        ),
        c(
          'koshiki-taki-otoshi-3',
          'Tori turns his body leftward and holds Uke’s hip with his left hand. Tori pushes Uke a few steps backward and leftward to break his balance. Uke losing his balance backward, tries to restore his position by slightly retreating with his left foot',
          'small_medium',
        ),
        c(
          'koshiki-taki-otoshi-4',
          'Tori changes the direction of his attack, moves his left hand to right side of Uke’s back, his right hand index finger between Uke’s collars and lift Uke’s body forward. Uke takes a step forward with his left foot',
          'small_medium',
        ),
        c(
          'koshiki-taki-otoshi-5',
          'Tori turns his body inside, retreats his left foot beside his right foot. Tori takes a few small steps backward. Uke is thrown diagonally over Tori’s body while Tori spreads both arms and legs on the ground (Dai-no-ji)',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-mi-kudaki',
      kind: 'technique',
      name: 'Mi-kudaki',
      nameJa: '身砕',
      techniqueId: 'koshiki-mi-kudaki',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-mi-kudaki-1',
          'Tori stands one step behind where he started Yume-no-uchi, in shizen-hontai toward Shomen. Uke stands on the same spot where he started Yume-no-uchi in shizen-hontai facing Tori',
          'small_medium',
        ),
        c(
          'koshiki-mi-kudaki-2',
          'Uke moves forward, starting with his left foot first, does 6 steps and puts his right foot in front of Tori’s left foot (with his right foot right before the Tori’s left foot). Uke grips the back side of Tori’s belt with his right hand and the front side with his left hand. Uke attempts to hit Tori’s right kneecap with his left foot. Uke swings back his left foot and strongly pulls both hands',
          'small_medium',
        ),
        c(
          'koshiki-mi-kudaki-3',
          'Tori grips over Uke’s left wrist with his right hand to release Uke’s left hand. Tori turns around his left hand (with small finger side facing upward), inserting his left arm under Uke’s left armpit, pushing Uke’s body backward and upward, Tori takes a few small steps with his left foot by tsugi-ashi leftward and backward',
          'small_medium',
        ),
        c(
          'koshiki-mi-kudaki-4',
          'Uke losing his balance backward, tries to restore his position by slightly retreating with his left foot. Uke takes a step forward with his left foot. Tori changes the direction of his attack, moves his right foot into the outside of Uke’s right foot and renders his body on his back underneath Uke’s legs',
          'small_medium',
        ),
        c(
          'koshiki-mi-kudaki-5',
          'Uke is thrown diagonally over Tori’s body and stands up',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-kuruma-gaeshi',
      kind: 'technique',
      name: 'Kuruma-gaeshi',
      nameJa: '車返',
      techniqueId: 'koshiki-kuruma-gaeshi',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-kuruma-gaeshi-1',
          'Tori and Uke stand on the diagonal line, Tori on the left side of Shomen, facing each other and quickly get closer. Uke thrusts both of Tori’s shoulders with both hands. Tori puts both of his hands on the bottom side of Uke’s upper arms, thumbs outward',
          'small_medium',
        ),
        c(
          'koshiki-kuruma-gaeshi-2',
          'Tori steps forward to the outside of Uke’s right foot with his left then right foot and renders his body on his back. Uke is thrown diagonally over Tori’s body and stands up',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-mizu-iri',
      kind: 'technique',
      name: 'Mizu-iri',
      nameJa: '水入',
      techniqueId: 'koshiki-mizu-iri',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-mizu-iri-1',
          'Tori and Uke stand on the diagonal line, Tori stands on the left side of Shomen facing each other and quickly get closer. Uke steps forward with his right foot and thrusts the left side of Tori’s shoulder with his right hand',
          'small_medium',
        ),
        c(
          'koshiki-mizu-iri-2',
          'Tori turns his left palm outward to grip Uke’s right wrist from its inward and his right palm upward (with thumb facing inside) underneath Uke’s right armpit. Tori steps into the outside of Uke’s right foot with his left and then right foot and renders his body on his back. Uke is thrown diagonally over Tori’s body and stands up',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-ryusetsu',
      kind: 'technique',
      name: 'Ryu-setsu',
      nameJa: '柳雪',
      techniqueId: 'koshiki-ryusetsu',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-ryusetsu-1',
          'Tori and Uke stand on the diagonal line, Tori stands on the right side of Shomen facing each other. Uke takes a few step forward and Tori takes a few quick steps forward to get closer to each other. Tori steps forward with his right foot and raises his right hand with its back facing upward (while lowering his fingertips), toward Uke’s face (eyes)',
          'small_medium',
        ),
        c(
          'koshiki-ryusetsu-2',
          'Uke leans back slightly with his head. Tori grips Uke’s left side of collar with his right hand and inserts his left hand under Uke’s right armpit',
          'small_medium',
        ),
        c(
          'koshiki-ryusetsu-3',
          'Tori steps into the outside of Uke’s right foot with his left and then right foot and renders his body on his back. Uke is thrown diagonally over Tori’s body and stands up',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-sakaotoshi',
      kind: 'technique',
      name: 'Saka-otoshi',
      nameJa: '坂落',
      techniqueId: 'koshiki-sakaotoshi',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-sakaotoshi-1',
          'Tori and Uke stand on the diagonal line, Tori stands on the left side of Shomen facing each other. Uke takes a few steps forward and Tori takes a few quick steps forward to get closer to each other',
          'small_medium',
        ),
        c(
          'koshiki-sakaotoshi-2',
          'Uke steps forward with his left foot and thrusts into Tori’s abdomen (suigetsu) sharply with his left hand as Te-gatana (with thumb facing upward and four fingers held together and stretched). Tori takes a step backward with his right foot and assumes left stance',
          'small_medium',
        ),
        c(
          'koshiki-sakaotoshi-3',
          'Tori grips over Uke’s wrist of thrusting hand from above with his right hand and puts his left palm (with thumb facing upward) inside of Uke’s left upper arm. Tori keeps his standing position and pulls down Uke. Uke turns over laterally forward',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-yukiore',
      kind: 'technique',
      name: 'Yuki-ore',
      nameJa: '雪折',
      techniqueId: 'koshiki-yukiore',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-yukiore-1',
          'Tori takes a few steps toward Uke and when Uke stands up, Tori turns his back toward Uke and walks forward. Uke follows Tori from behind',
          'small_medium',
        ),
        c(
          'koshiki-yukiore-2',
          'Uke steps outside of Tori’s right foot with his right foot, attempts to hold Tori’s body with his arms over both of Tori’s arms. Tori opens both arms and holds Uke’s right arm from above (with his right hand on Uke’s upper arm and his left hand on Uke’s forearm)',
          'small_medium',
        ),
        c(
          'koshiki-yukiore-3',
          'Tori lowers his body by putting his right knee on the tatami to throw Uke over his back. Uke turns over forward',
          'small_medium',
        ),
      ],
    },
    {
      id: 'koshiki-iwa-nami',
      kind: 'technique',
      name: 'Iwa-nami',
      nameJa: '岩波',
      techniqueId: 'koshiki-iwa-nami',
      groupId: 'ura',
      criteria: [
        c(
          'koshiki-iwa-nami-1',
          'Uke stands up quickly to face Tori. Tori takes a step or two toward Uke. Tori raises both of his hands with their backs facing upward toward Uke’s face to apply a blinding-strike into Uke’s eyes. Uke leans back slightly with his head',
          'small_medium',
        ),
        c(
          'koshiki-iwa-nami-2',
          'Uke brings back his head, then Tori grips both of Uke’s side collars with both hands. Tori lifts Uke’s body to break his balance',
          'small_medium',
        ),
        c(
          'koshiki-iwa-nami-3',
          'Tori steps into the outside of Uke’s right foot with his left and then right feet, renders his body on his back. Uke is thrown diagonally over Tori’s body while Tori spreads both arms and legs on the ground (Dai-no-ji)',
          'small_medium',
        ),
      ],
    },
  ],
}
